import { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';

import { useDeviceModals } from '@/feature/device/hooks/useDeviceModals';
import { deviceService } from '@/feature/device/services/deviceService';
import type {
  ICreateDeviceRequest,
  IDeviceListItem,
  IDeviceSummary,
} from '@/feature/device/types/device';
import GenericFilter from '@/shared/components/data-display/GenericFilter';
import { TanStackTable } from '@/shared/components/data-display/TanStackTable';
import { useServerListTableState } from '@/shared/hooks/useServerListTableState';

import { ROUTES } from '../../routes/routeConstants';

import AddDeviceModal from './AddDeviceModal';
import { getDeviceColumns } from './deviceColumns';

const EMPTY_SUMMARY: IDeviceSummary = {
  total: 0,
  available: 0,
  rented: 0,
  maintenance: 0,
  unfit: 0,
};

const STATUS_FILTER_OPTIONS = ['All', 'Available', 'Rented', 'Maintenance', 'Unfit'];

const DevicesPage = () => {
  const [devices, setDevices] = useState<IDeviceListItem[]>([]);
  const [summary, setSummary] = useState<IDeviceSummary>(EMPTY_SUMMARY);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('All');
  const [isAddOpen, setIsAddOpen] = useState(false);

  const { listParams, page, setPage, pageSize, search, setSearch, onSortChange, resetPage } =
    useServerListTableState({ pageSize: 10 });

  const fetchDevices = useCallback(async () => {
    setIsLoading(true);
    const params: Record<string, unknown> = { ...listParams };
    if (statusFilter !== 'All') {
      params.device_status = statusFilter.toLowerCase();
    }
    const result = await deviceService.getDevices(params);
    setDevices(result.devices || []);
    setSummary(result.summary || EMPTY_SUMMARY);
    setTotalCount(result.pagination?.total_count ?? result.devices?.length ?? 0);
    setIsLoading(false);
  }, [listParams, statusFilter]);

  useEffect(() => {
    void fetchDevices();
  }, [fetchDevices]);

  const { handleAction, modalElements } = useDeviceModals(fetchDevices);

  const columns = useMemo(() => getDeviceColumns({ onAction: handleAction }), [handleAction]);

  const handleStatusChange = (value: string) => {
    setStatusFilter(value);
    resetPage();
  };

  const handleAddDevice = async (data: ICreateDeviceRequest) => {
    const result = await deviceService.createDevice(data);
    if (result) {
      toast.success(result.message || 'Device added successfully.');
      await fetchDevices();
      return true;
    }
    return false;
  };

  return (
    <div className='px-6 pt-3 pb-6 bg-gray-50 min-h-full'>
      <div className='flex items-center justify-between mb-6'>
        <nav className='flex items-center gap-2 text-sm'>
          <Link
            to={ROUTES.DASHBOARD}
            className='flex items-center text-theme-500 hover:opacity-80 transition-opacity [&_svg]:stroke-theme-500'
          >
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='16'
              height='16'
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
              aria-hidden='true'
            >
              <path d='M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8'></path>
              <path d='M3 10a2 2 0 0 1 .709-1.528l7-5.999a2 2 0 0 1 2.582 0l7 5.999A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z'></path>
            </svg>
          </Link>
          <span className='text-gray-400 text-base'>›</span>
          <span className='text-gray-500 font-normal'>Devices</span>
        </nav>
        <button
          type='button'
          className='bg-theme-500 text-white border-none px-5 py-2.5 rounded-md text-sm font-medium cursor-pointer hover:bg-theme-600 transition-colors whitespace-nowrap'
          onClick={() => setIsAddOpen(true)}
        >
          <span>
            + <strong>Add Device</strong>
          </span>
        </button>
      </div>

      <div className='grid grid-cols-2 md:grid-cols-5 gap-3 mb-6'>
        <SummaryCard label='Total Devices' value={summary.total} />
        <SummaryCard label='Available' value={summary.available} accent='text-emerald-700' />
        <SummaryCard label='Rented' value={summary.rented} accent='text-blue-700' />
        <SummaryCard label='Maintenance' value={summary.maintenance} accent='text-amber-700' />
        <SummaryCard label='Unfit' value={summary.unfit} accent='text-red-700' />
      </div>

      <TanStackTable<IDeviceListItem>
        data={devices}
        columns={columns}
        loading={isLoading}
        emptyMessage='No devices found.'
        searchPlaceholder='Search devices...'
        getRowId={(row) => row.id || row.device_id}
        customFilters={
          <GenericFilter
            label='Status'
            value={statusFilter}
            options={STATUS_FILTER_OPTIONS}
            onChange={handleStatusChange}
          />
        }
        serverSidePagination={true}
        totalItems={totalCount}
        currentPage={page}
        onPageChange={setPage}
        pageSize={pageSize}
        serverSideSearch={true}
        serverSearchValue={search}
        onServerSearchChange={setSearch}
        manualSorting={true}
        onServerSortChange={(sorting) => {
          if (sorting.length > 0) {
            onSortChange(sorting[0].id, sorting[0].desc ? 'desc' : 'asc');
          }
        }}
        resetStateOnDataChange={false}
      />

      <AddDeviceModal
        isOpen={isAddOpen}
        onClose={() => setIsAddOpen(false)}
        onAdd={handleAddDevice}
      />
      {modalElements}
    </div>
  );
};

function SummaryCard({
  label,
  value,
  accent = 'text-gray-900',
}: {
  label: string;
  value: number;
  accent?: string;
}) {
  return (
    <div className='bg-white border border-gray-200 rounded-xl px-4 py-3'>
      <div className='text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1'>
        {label}
      </div>
      <div className={`text-2xl font-bold ${accent}`}>{value}</div>
    </div>
  );
}

export default DevicesPage;
